import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Users, Trash2, Check, ChevronDown, ChevronRight, ShieldCheck, RefreshCw, Activity, Clock } from "lucide-react";
import { sr } from "../../lib/sr";

const INTENTS = [
  ["target", "Target them"],
  ["lookalike", "Build a lookalike"],
  ["suppress", "Suppress (exclude)"],
];

const SyncBadge = ({ s }) => {
  if (!s || !s.status) return null;
  const map = {
    synced: ["sr-badge-green", "Synced"],
    processing: ["sr-badge-amber", "Processing"],
    error: ["sr-badge-red", "Error"],
  };
  const [cls, label] = map[s.status] || ["sr-badge-gray", s.status];
  return <span className={`sr-badge ${cls}`} style={{ fontSize: 10 }}>{label}</span>;
};

/**
 * Upload-your-own customer list → match-ready Custom Audience / Customer Match.
 * PII is SHA-256 hashed server-side (never stored raw). Reused in the Audience Builder,
 * the Growth Plan audience step and the dedicated Customer Lists page.
 */
export default function CustomerListManager({ embedded = false }) {
  const [open, setOpen] = useState(!embedded);
  const [lists, setLists] = useState([]);
  const [googleReady, setGoogleReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [intents, setIntents] = useState(["target"]);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState("");
  const [syncBusy, setSyncBusy] = useState("");

  const load = () => {
    setLoading(true);
    sr.customerLists()
      .then((r) => { setLists(r.lists || []); setGoogleReady(!!r.google_ready); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (open) load(); }, [open]);

  const toggleIntent = (k) => setIntents((xs) => (xs.includes(k) ? xs.filter((x) => x !== k) : [...xs, k]));

  const upload = async () => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    if (!consent) { toast.error("Please confirm you have consent to use this list"); return; }
    const fd = new FormData();
    fd.append("name", name || file.name.replace(/\.csv$/i, ""));
    fd.append("intents", (intents.length ? intents : ["target"]).join(","));
    fd.append("consent", "true");
    fd.append("file", file);
    setBusy("upload");
    try {
      const r = await sr.uploadCustomerList(fd);
      toast.success(`Uploaded "${r.list.name}" — ${r.list.row_count.toLocaleString()} customers (hashed)`);
      setName(""); setFile(null); setConsent(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(""); }
  };

  const syncMeta = async (id) => {
    setSyncBusy(`meta-${id}`);
    try { const r = await sr.syncCustomerListMeta(id); toast.success("Pushed to Meta Custom Audiences"); setLists((xs) => xs.map((l) => (l.id === id ? { ...l, meta: r.meta } : l))); }
    catch (e) { toast.error(e?.response?.data?.detail || "Meta sync failed"); }
    finally { setSyncBusy(""); }
  };
  const syncGoogle = async (id) => {
    setSyncBusy(`google-${id}`);
    try { const r = await sr.syncCustomerListGoogle(id); toast.success("Pushed to Google Customer Match"); setLists((xs) => xs.map((l) => (l.id === id ? { ...l, google: r.google } : l))); }
    catch (e) { toast.error(e?.response?.data?.detail || "Google sync failed"); }
    finally { setSyncBusy(""); }
  };
  const remove = async (id) => {
    setBusy(`del-${id}`);
    try { await sr.deleteCustomerList(id); setLists((xs) => xs.filter((l) => l.id !== id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
    finally { setBusy(""); }
  };
  const refreshStatus = async (id) => {
    setSyncBusy(`refresh-${id}`);
    try { const r = await sr.refreshCustomerListStatus(id); setLists((xs) => xs.map((l) => (l.id === id ? r.list : l))); toast.success("Match status updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't refresh status"); }
    finally { setSyncBusy(""); }
  };

  const Body = (
    <div style={{ marginTop: embedded ? 10 : 0 }} data-testid="customer-lists-panel">
      {/* Upload form */}
      <div style={{ display: "grid", gap: 8, padding: 12, background: "#F7FAFF", border: "1px solid #DCE6F5", borderRadius: 10 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input className="sr-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="List name (e.g. VIP customers)" data-testid="cl-name-input" style={{ flex: "1 1 180px", minWidth: 0 }} />
          <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="cl-file-input" style={{ fontSize: 12 }} />
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <span className="sr-muted" style={{ fontSize: 11.5 }}>Use for:</span>
          {INTENTS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => toggleIntent(k)} data-testid={`cl-intent-${k}`}
              style={{ border: "1px solid " + (intents.includes(k) ? "var(--blue)" : "var(--border)"), background: intents.includes(k) ? "var(--blue)" : "#fff", color: intents.includes(k) ? "#fff" : "var(--navy)", borderRadius: 999, padding: "4px 10px", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
              {label}
            </button>
          ))}
        </div>
        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 11.5, color: "var(--muted)" }} data-testid="cl-consent-label">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="cl-consent" style={{ marginTop: 2 }} />
          <span>I confirm these customers consented to marketing and I may use this data for ad targeting on Meta & Google. Emails/phones are <strong>SHA-256 hashed</strong> before storage — we never keep raw contact details.</span>
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={upload} disabled={busy === "upload"} data-testid="cl-upload-btn">
            {busy === "upload" ? <Loader2 size={13} className="sr-spin" /> : <Upload size={13} />} Upload customer list
          </button>
          <span className="sr-muted" style={{ fontSize: 11 }}>CSV columns: <code>email</code> and/or <code>phone</code> (+ optional first_name, last_name, zip). Phones need a + country code for Google.</span>
        </div>
      </div>

      {/* Existing lists */}
      {loading ? (
        <div className="sr-muted" style={{ fontSize: 12.5, marginTop: 10 }}><Loader2 size={13} className="sr-spin" /> Loading…</div>
      ) : lists.length === 0 ? (
        <div className="sr-muted" style={{ fontSize: 12.5, marginTop: 10 }} data-testid="cl-empty">No customer lists yet — upload one above to target, model, or suppress your own customers.</div>
      ) : (
        <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
          {lists.map((l) => (
            <div key={l.id} data-testid={`cl-row-${l.id}`} style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 10, background: "#fff" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <Users size={14} style={{ color: "var(--blue)" }} />
                <strong style={{ color: "var(--navy)", fontSize: 13 }}>{l.name}</strong>
                <span className="sr-muted" style={{ fontSize: 11.5 }}>{l.row_count.toLocaleString()} customers · {l.email_count.toLocaleString()} emails · {l.phone_count.toLocaleString()} phones</span>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => remove(l.id)} disabled={busy === `del-${l.id}`} data-testid={`cl-delete-${l.id}`} style={{ marginLeft: "auto" }}>
                  {busy === `del-${l.id}` ? <Loader2 size={12} className="sr-spin" /> : <Trash2 size={12} />}
                </button>
              </div>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 6 }}>
                {(l.intents || []).map((it) => <span key={it} className="sr-badge sr-badge-gray" style={{ fontSize: 10 }}>{(INTENTS.find((x) => x[0] === it) || [it, it])[1]}</span>)}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
                <button className="sr-btn sr-btn-sm" onClick={() => syncMeta(l.id)} disabled={!!syncBusy} data-testid={`cl-sync-meta-${l.id}`} style={{ background: "#fff" }}>
                  {syncBusy === `meta-${l.id}` ? <Loader2 size={12} className="sr-spin" /> : <Check size={12} />} Push to Meta
                </button>
                <SyncBadge s={l.meta} />
                {googleReady ? (
                  <>
                    <button className="sr-btn sr-btn-sm" onClick={() => syncGoogle(l.id)} disabled={!!syncBusy} data-testid={`cl-sync-google-${l.id}`} style={{ background: "#fff" }}>
                      {syncBusy === `google-${l.id}` ? <Loader2 size={12} className="sr-spin" /> : <Check size={12} />} Push to Google
                    </button>
                    <SyncBadge s={l.google} />
                  </>
                ) : (
                  <span className="sr-badge sr-badge-gray" data-testid={`cl-google-soon-${l.id}`} style={{ fontSize: 10.5, display: "inline-flex", alignItems: "center", gap: 4 }} title="Google Customer Match is coming soon">
                    <Clock size={11} /> Google Customer Match — coming soon
                  </span>
                )}
                {l.meta?.lookalike_id && <span className="sr-badge sr-badge-green" style={{ fontSize: 10 }}>Lookalike created</span>}
              </div>
              {(l.meta?.audience_id || l.google?.user_list) && (
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 8, fontSize: 11.5 }} data-testid={`cl-matchrate-${l.id}`}>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => refreshStatus(l.id)} disabled={!!syncBusy} data-testid={`cl-refresh-${l.id}`}>
                    {syncBusy === `refresh-${l.id}` ? <Loader2 size={12} className="sr-spin" /> : <RefreshCw size={12} />} Refresh status
                  </button>
                  {l.meta?.audience_id && (
                    <span className="sr-muted" style={{ display: "inline-flex", alignItems: "center", gap: 5 }} data-testid={`cl-meta-size-${l.id}`}>
                      <Activity size={12} style={{ color: "var(--blue)" }} /> Meta: {typeof l.meta?.audience_size === "number" ? `~${l.meta.audience_size.toLocaleString()} matched` : (l.meta?.processing ? "processing…" : "awaiting size")}{typeof l.meta?.match_rate === "number" ? ` · ${Math.round(l.meta.match_rate * 100)}% match rate` : ""}
                    </span>
                  )}
                  {l.google?.user_list && (
                    <span className="sr-muted">Google: {typeof l.google?.audience_size === "number" ? `~${l.google.audience_size.toLocaleString()} matched` : "processing (24–48h)"}</span>
                  )}
                </div>
              )}
              {(l.meta?.error || l.google?.error) && <div style={{ fontSize: 11, color: "#B42318", marginTop: 6 }}>{l.meta?.error || l.google?.error}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (!embedded) return Body;

  return (
    <div style={{ marginTop: 12, border: "1px solid #DCE6F5", borderRadius: 10, padding: 10, background: "#fff" }} data-testid="customer-lists-embed">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="customer-lists-toggle"
        style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", background: "none", border: "none", cursor: "pointer", padding: 0, color: "var(--navy)" }}>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <ShieldCheck size={15} style={{ color: "var(--blue)" }} />
        <strong style={{ fontSize: 13.5 }}>My customers — upload your own list</strong>
        <span className="sr-muted" style={{ fontSize: 11.5, marginLeft: "auto" }}>{lists.length ? `${lists.length} list${lists.length > 1 ? "s" : ""}` : "Target / lookalike / suppress"}</span>
      </button>
      {open && Body}
    </div>
  );
}
